import { defaultLocale, hreflangAlternates, localeFromPathname, ogLocaleFor, supportedLocales } from '@shared/locales';
import { securityHeaders } from '@shared/security-headers';
import type { RouteMeta } from '../routes';

/**
 * Head tags, the sitemap, robots.txt and the static `_headers` rules, built
 * from the same `RouteMeta` the prerenderer writes to disk. Kept here (rather
 * than inline in scripts/prerender.mjs) so unit tests can assert hreflang,
 * canonicals and the sitemap without running the full build.
 */

/** The social preview card for each language: its text is baked into the image. */
export const OG_IMAGES = {
  en: {
    path: '/images/og-image.png',
    width: 1200,
    height: 630,
    alt: 'Landmate — rental property management software, in early development',
  },
  es: {
    path: '/images/og-image-es.png',
    width: 1200,
    height: 630,
    alt: 'Landmate: software de administración de alquileres, en una etapa temprana de desarrollo',
  },
} as const satisfies Record<string, { path: string; width: number; height: number; alt: string }>;

export function ogImageFor(locale: string) {
  return locale === 'es' ? OG_IMAGES.es : OG_IMAGES.en;
}

export function escapeHtml(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');
}

export function escapeXml(value: string): string {
  return String(value).replace(
    /[<>&'"]/g,
    (c) => `&${{ '<': 'lt', '>': 'gt', '&': 'amp', "'": 'apos', '"': 'quot' }[c]};`,
  );
}

/** Where the Markdown twin of a page lives. `/` becomes /index.md; every other route gains a suffix. */
export const markdownPathFor = (routePath: string) => (routePath === '/' ? '/index.md' : `${routePath}.md`);

export function canonicalUrl(siteUrl: string, path: string): string {
  return `${siteUrl}${path === '/' ? '/' : path}`;
}

/** `Page title · Landmate`, or the site name first on the home page. */
export function documentTitle(meta: Pick<RouteMeta, 'path' | 'title'>, siteName: string): string {
  const { pathname } = localeFromPathname(meta.path);
  return pathname === '/' ? `${siteName} — ${meta.title}` : `${meta.title} · ${siteName}`;
}

export interface HeadOptions {
  noindex?: boolean;
  /**
   * A non-production build: every page also says noindex, while keeping its
   * canonical and hreflang so the build matches production apart from that.
   */
  nonProduction?: boolean;
  /** Whether a Markdown twin was written for this page. */
  hasMarkdown?: boolean;
}

export function headFor(
  meta: RouteMeta,
  site: { siteUrl: string; siteName: string },
  { noindex: noindexOption = false, hasMarkdown = false, nonProduction = false }: HeadOptions = {},
): string {
  const noindex = noindexOption || meta.noindex === true;
  const { locale, pathname: unprefixed } = localeFromPathname(meta.path);
  const canonical = canonicalUrl(site.siteUrl, meta.path);
  const title = documentTitle(meta, site.siteName);
  const ogImage = ogImageFor(locale);
  const tags = [
    `<title>${escapeHtml(title)}</title>`,
    `<meta name="description" content="${escapeHtml(meta.description)}" />`,
    noindex ? '<meta name="robots" content="noindex" />' : `<link rel="canonical" href="${canonical}" />`,
    !noindex && nonProduction ? '<meta name="robots" content="noindex" />' : '',
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="${escapeHtml(site.siteName)}" />`,
    `<meta property="og:title" content="${escapeHtml(title)}" />`,
    `<meta property="og:description" content="${escapeHtml(meta.description)}" />`,
    `<meta property="og:url" content="${canonical}" />`,
    `<meta property="og:image" content="${site.siteUrl}${ogImage.path}" />`,
    `<meta property="og:image:width" content="${ogImage.width}" />`,
    `<meta property="og:image:height" content="${ogImage.height}" />`,
    `<meta property="og:image:alt" content="${escapeHtml(ogImage.alt)}" />`,
    `<meta property="og:locale" content="${ogLocaleFor(locale)}" />`,
    ...supportedLocales
      .filter((other) => other !== locale)
      .map((other) => `<meta property="og:locale:alternate" content="${ogLocaleFor(other)}" />`),
    ...(noindex
      ? []
      : hreflangAlternates(unprefixed, site.siteUrl).map(
          (link) => `<link rel="alternate" hreflang="${link.hreflang}" href="${link.href}" />`,
        )),
    `<meta name="twitter:card" content="summary_large_image" />`,
  ].filter(Boolean);
  if (!noindex && hasMarkdown) {
    tags.push(`<link rel="alternate" type="text/markdown" href="${site.siteUrl}${markdownPathFor(meta.path)}" />`);
  }
  if (meta.jsonLd) {
    tags.push(`<script type="application/ld+json">${JSON.stringify(meta.jsonLd).replaceAll('<', '\\u003c')}</script>`);
  }
  return tags.join('\n    ');
}

/**
 * Google's multilingual sitemap: every localized URL, each carrying
 * `xhtml:link rel="alternate"` for every language variant (plus x-default).
 */
export function sitemapXml(routes: RouteMeta[], siteUrl: string): string {
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"',
    '        xmlns:xhtml="http://www.w3.org/1999/xhtml">',
    ...routes
      .filter((meta) => !meta.noindex)
      .map((meta) => {
        const unprefixed = localeFromPathname(meta.path).pathname;
        return [
          '  <url>',
          `    <loc>${escapeXml(canonicalUrl(siteUrl, meta.path))}</loc>`,
          ...(meta.lastmod ? [`    <lastmod>${meta.lastmod}</lastmod>`] : []),
          ...hreflangAlternates(unprefixed, siteUrl).map(
            (link) => `    <xhtml:link rel="alternate" hreflang="${link.hreflang}" href="${escapeXml(link.href)}" />`,
          ),
          '  </url>',
        ].join('\n');
      }),
    '</urlset>',
    '',
  ].join('\n');
}

/**
 * robots.txt. Only a production build lets crawlers in; preview and local
 * builds disallow everything, so a deployment nobody should find never ends
 * up in an index.
 *
 * The Content-Signal line (https://contentsignals.org) says: index and link
 * to this site, quote it in AI answers, and use it for AI training (the
 * owner's decision). No crawler, AI or otherwise, is singled out.
 */
export function robotsTxt({ production, siteUrl }: { production: boolean; siteUrl: string }): string {
  if (!production) {
    return [
      '# Generated by scripts/prerender.mjs. Not a production build: no crawling.',
      'User-agent: *',
      'Disallow: /',
      '',
    ].join('\n');
  }
  return [
    '# Generated by scripts/prerender.mjs — edit src/web/lib/seo.ts, not this file.',
    'User-agent: *',
    'Content-Signal: search=yes, ai-input=yes, ai-train=yes',
    'Allow: /',
    'Disallow: /admin',
    'Disallow: /auth',
    'Disallow: /api',
    '',
    `Sitemap: ${siteUrl}/sitemap.xml`,
    '',
  ].join('\n');
}

/**
 * The generated half of dist/_headers: security headers for whatever Static
 * Assets answers without the Worker, and content types for the Markdown twins.
 * The hand-written half (cache policy) is public/_headers.
 */
export function generatedHeaderRules({
  production,
  noindexMarkdown = [],
}: {
  production: boolean;
  /** Markdown twins of noindex pages (draft legal pages): they carry the noindex as a header. */
  noindexMarkdown?: string[];
}): string {
  const site = securityHeaders('site', { production });
  return [
    '# ---------------------------------------------------------------------------',
    '# Generated by scripts/prerender.mjs. Everything above is hand-written.',
    '# Security headers mirror src/shared/security-headers.ts (the Worker sets the',
    '# same ones on everything it answers). None of these set Cache-Control, so',
    '# they never join a cache rule above.',
    '# ---------------------------------------------------------------------------',
    '',
    '/*',
    ...Object.entries(site).map(([name, value]) => `  ${name}: ${value}`),
    // Only production may be indexed (the Worker sends the same on everything it answers).
    ...(production ? [] : ['  X-Robots-Tag: noindex']),
    '',
    '/*.md',
    '  Content-Type: text/markdown; charset=utf-8; variant=GFM',
    '',
    ...supportedLocales.flatMap((locale) => {
      const suffix = locale === defaultLocale ? '' : `.${locale}`;
      return [`/llms${suffix}.txt`, `/llms-full${suffix}.txt`, `/llms-docs${suffix}.txt`].flatMap((file) => [
        file,
        '  Content-Type: text/markdown; charset=utf-8',
        '',
      ]);
    }),
    ...noindexMarkdown.flatMap((file) => [file, '  X-Robots-Tag: noindex', '']),
  ].join('\n');
}

export { defaultLocale };
